import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { coachSend, coachHistory, coachJourney } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Send, Loader2, Brain, ArrowRight, Compass, MessageSquare } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import FitFinder from "@/components/wishire/FitFinder";

/* The AI coach — a fresher's companion from day one to placement. The journey
   header (deterministic, from /coach/journey) shows where they are and the next
   best moves; the chat coach sees the same journey and can attach action chips
   that deep-link into the platform. */

const SUGGESTIONS_PRO = [
  "Should I take a startup offer at 30% less base?",
  "What's missing on my path to Senior Engineer?",
  "How do I negotiate a counter offer?",
  "What skills should I learn for AI engineer roles?",
];

// Fallback if the journey hasn't loaded — stage-aware ones replace these.
const SUGGESTIONS_STUDENT = [
  "What should I focus on this month to get placed?",
  "How do I raise my Wishire Score fastest?",
  "I get nervous in interviews — how do I fix that?",
  "Which skills matter most for my target role right now?",
];

const STAGE_TONE = {
  setup: "bg-stone-100 text-stone-700 border-stone-200",
  baseline: "bg-blue-50 text-blue-700 border-blue-200",
  build: "bg-amber-50 text-amber-700 border-amber-200",
  drive_season: "bg-[#C2410C]/10 text-[#C2410C] border-[#C2410C]/30",
  interview_ready: "bg-emerald-50 text-emerald-700 border-emerald-200",
  placed: "bg-emerald-100 text-emerald-800 border-emerald-300",
};

export default function CoachPage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const mode = params.get("mode") === "fit" ? "fit" : "chat";
  const setMode = (m) => { const n = new URLSearchParams(params); if (m === "fit") n.set("mode", "fit"); else n.delete("mode"); setParams(n, { replace: true }); };
  const [messages, setMessages] = useState([]);
  const [journey, setJourney] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = `coach_${user?.user_id || "anon"}`;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await coachHistory();
        const flat = [];
        data.forEach((m) => {
          flat.push({ role: "user", text: m.user_message });
          flat.push({ role: "assistant", text: m.assistant_message, actions: m.actions || [] });
        });
        setMessages(flat);
      } catch (e) {}
    })();
    coachJourney().then(({ data }) => setJourney(data)).catch(() => {});
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async (text) => {
    const t = (text ?? input).trim();
    if (!t || sending) return;
    setMessages((m) => [...m, { role: "user", text: t }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await coachSend(t, sessionId);
      setMessages((m) => [...m, { role: "assistant", text: data.reply, actions: data.actions || [] }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: "assistant", text: "Sorry, I'm temporarily unavailable. Try again in a moment.", actions: [] },
      ]);
    } finally {
      setSending(false);
    }
  };

  const starters = journey?.starter_questions?.length
    ? journey.starter_questions
    : user?.college_id ? SUGGESTIONS_STUDENT : SUGGESTIONS_PRO;

  return (
    <div className="container-x py-10 md:py-14">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="overline">Ask your coach</span>
          <h1 className="font-display text-4xl md:text-5xl mt-3 leading-tight">
            {mode === "fit" ? <>Which role is <span className="italic text-stone-500">right for you?</span></> : <>Your coach, from day one{" "}<span className="italic text-stone-500">to placement day.</span></>}
          </h1>
        </div>
        <div className="inline-flex p-1 rounded-full border border-stone-300 bg-white">
          {[["chat", "Chat", MessageSquare], ["fit", "Find my fit", Compass]].map(([k, l, I]) => (
            <button key={k} onClick={() => setMode(k)} data-testid={`coach-mode-${k}`} className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-all ${mode === k ? "bg-stone-900 text-stone-50" : "text-stone-600 hover:text-stone-900"}`}><I size={14} /> {l}</button>
          ))}
        </div>
      </div>

      {mode === "fit" ? <FitFinder /> : (
      <div className="rounded-[2rem] border border-stone-200 bg-white overflow-hidden flex flex-col h-[68vh]">
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto p-6 md:p-10 space-y-6"
          data-testid="coach-messages"
        >
          {messages.length === 0 && (
            <div className="text-center max-w-xl mx-auto py-12">
              <div className="inline-flex p-4 rounded-2xl bg-amber-100 text-[#C2410C] mb-5">
                <Brain size={24} />
              </div>
              <h2 className="font-display text-3xl">Ask me anything about getting placed.</h2>
              <p className="mt-3 text-stone-600 leading-relaxed">
                I can see your score, your resume, your interviews and your campus drives —
                so my answers are about <em>you</em>, not a textbook.
              </p>
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3 text-left">
                {starters.map((s, i) => (
                  <button
                    key={i}
                    data-testid={`coach-suggest-${i}`}
                    onClick={() => send(s)}
                    className="text-sm px-4 py-3 rounded-2xl border border-stone-200 hover:bg-stone-50 hover:border-stone-900 transition-colors text-stone-800 text-left"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <Bubble key={i} role={m.role} text={m.text} actions={m.actions} />
          ))}
          {sending && (
            <Bubble role="assistant" text="…" loading />
          )}
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
          className="border-t border-stone-200 p-4 md:p-5 flex items-center gap-3 bg-[#F5F3ED]"
        >
          <input
            type="text"
            data-testid="coach-input"
            placeholder="Type your question…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={sending}
            className="flex-1 px-5 py-3 rounded-full bg-white border border-stone-300 placeholder:text-stone-400 focus:outline-none focus:border-stone-900 text-sm"
          />
          <button
            type="submit"
            data-testid="coach-send"
            disabled={sending || !input.trim()}
            className="btn-primary disabled:opacity-60"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </button>
        </form>
      </div>
      )}
    </div>
  );
}

function Bubble({ role, text, actions, loading }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[80%] md:max-w-[70%]`}>
        <div
          className={`rounded-3xl px-5 py-3.5 leading-relaxed ${
            isUser
              ? "bg-stone-900 text-stone-50"
              : "bg-[#FEF3C7] text-stone-900 border border-amber-100"
          } ${loading ? "italic text-stone-500" : ""}`}
        >
          {(text || "").split("\n").map((line, i) => (
            <p key={i} className={i > 0 ? "mt-2" : ""}>
              {line}
            </p>
          ))}
        </div>
        {/* Action chips — the coach's advice, one tap away */}
        {!isUser && actions?.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {actions.map((a, i) => (
              <Link key={i} to={a.path} data-testid={`coach-chip-${i}`}
                className="inline-flex items-center gap-1.5 text-xs font-medium px-3.5 py-2 rounded-full bg-stone-900 text-stone-50 hover:bg-[#C2410C] transition-colors">
                {a.label} <ArrowRight size={12} />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
